import React from "react";
import styled from "styled-components";

import { VSpace } from "@examspark/common-ui";
import {
  CardLink,
  LinkCard,
} from "@examspark/common-ui/dist/components/containers/LinkCard";
import { Maybe, Resource } from "@examspark/types";

import { getUrlForObject } from "../../utils";
import BankBadge from "../common/BankBadge";
import magnifyingGlass from "../common/graphics/magnifying-glass.svg";
import UnitContent from "../common/UnitContent";

import ItemMetadata from "./ItemMetadata";
import { SearchResultUpdater } from "./useSearch";

const Results = styled(VSpace)`
  gap: 0.6rem;
`;

const EmptyBox = styled.div`
  border: dashed 2px #ddd;
  border-radius: 0.5rem;
  padding: 5rem 3rem;
  align-items: center;
  justify-content: center;
  display: flex;

  flex-direction: column;
  gap: 2rem;

  h1 {
    font-size: 3rem;
    font-weight: 300;
    color: #333;
    margin: 0;
  }
  svg {
    fill: #888;
    width: 6.4rem;
  }
`;

const ResultTitle = styled.h3`
  color: #6450c2;
  font-weight: inherit;
  font-size: inherit;
`;

const Result = styled(LinkCard)`
  padding: 1rem 1rem;
  color: #333;
  gap: 1rem;
  & ${CardLink}:hover, & ${CardLink}:focus-visible {
    text-decoration: underline;
  }
`;

const ResultTop = styled.header`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const ResultMain = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ResultType = styled.div`
  color: #888;
`;

const NoResultsText = styled.span`
  font-size: 3rem;
`;

const RevisionNoteList = ({
  data,
  query,
  update,
  noResultsPlaceholder,
  onResultSelect,
}: {
  data?: Array<Maybe<Resource>>;
  query?: string;
  update?: SearchResultUpdater;
  noResultsPlaceholder?: string;
  onResultSelect?: () => void;
}) => {
  return (
    <Results>
      {!data || !data.length ? (
        <EmptyBox>
          <img alt="Searching" src={magnifyingGlass} width="64" />
          <NoResultsText>
            {noResultsPlaceholder ?? "No search results."}
          </NoResultsText>
        </EmptyBox>
      ) : (
        data.map((item: Maybe<Resource>) => {
          if (!item || !item?.resource) return null;
          if (
            query &&
            !item.name?.toLowerCase().includes(query.toLowerCase())
          ) {
            return null;
          }

          const url = getUrlForObject(item?.resource);

          return (
            <Result key={item.id}>
              <ResultTop>
                <ResultMain>
                  <ResultTitle>
                    {url == null ? (
                      item.resource?.name
                    ) : (
                      <CardLink to={url} onClick={onResultSelect}>
                        {item.resource?.name}
                      </CardLink>
                    )}
                  </ResultTitle>
                </ResultMain>

                <ResultType>Revision</ResultType>
                <BankBadge bankId={item.bankId} />
              </ResultTop>
              <UnitContent
                content={item.content?.body ?? item.description ?? ""}
              />
              <ItemMetadata item={item} />
            </Result>
          );
        })
      )}
    </Results>
  );
};

export default RevisionNoteList;
